package team.platforma.chat_data.repository.messages

import io.ktor.client.HttpClient
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.request
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpMethod
import io.ktor.http.contentType
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import team.platforma.accountmanagerlib.getAccountManager
import team.platforma.chat_data.database.messages.MokMessagesTable
import team.platforma.chat_data.model.ChatData
import team.platforma.chat_data.model.SendMessageData
import team.platforma.chat_data.network.body.CreateChatBody
import team.platforma.chat_data.network.body.MessageBody
import team.platforma.chat_data.network.response.CreateChatResponse
import team.platforma.chat_data.network.response.MessageResponse
import team.platforma.chat_data.repository.chats.ChatsRepository
import team.platforma.chatdata.table.MokMessage
import team.platforma.wrapper.Response
import team.platforma.wrapper.ResponseWrapper
import kotlin.coroutines.cancellation.CancellationException

class MokMessagesRepositoryImpl(
    private val mokMessagesTable: MokMessagesTable,
    private val client: HttpClient,
    private val chatsRepository: ChatsRepository
) : MokMessagesRepository, ResponseWrapper() {

    private val pollingScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private val pullingManager = ChatPollingManager()

    override suspend fun createNewChat(modelId: Long, chatId: Long, text: String): Response<Long> {
        val account = getAccountManager()

        val messageTableId = mokMessagesTable.createNewTableMessage(
            SendMessageData(
                chatId = chatId,
                aiModelId = modelId,
                userID = account.getUserId(),
                textMessage = text,
                senderId = account.getUserId(),
                status = "Delivered"
            )
        )
        val result = wrapResponse<CreateChatResponse>(
            client.request("web-api/chat") {
                contentType(ContentType.Application.Json)
                header("Authorization", "Bearer ${account.getUserAuthToken()}")
                setBody(
                    CreateChatBody(
                        title = text,
                        aiModelId = modelId
                    )
                )
                method = HttpMethod.Post
            }
        )

        return when (result) {
            is Response.Success -> {
                mokMessagesTable.updateMessage(
                    chatId = result.data.chatId,
                    tableMessageId = messageTableId,
                    status = "Delivered"
                )
                sendMessageToAi(aiModelId = modelId, result.data.chatId, text, newChat = true)
                Response.Success(data = result.data.chatId, statusCode = result.statusCode)
            }

            is Response.Fail -> Response.Fail(result.message, result.statusCode)
            is Response.Exception -> Response.Exception(result.message, result.statusCode)
        }

    }

    override suspend fun sendActiveChatMessage(
        modelId: Long,
        chatId: Long,
        userMessage: String,
        aiContext: String
    ) {
        val account = getAccountManager()

        mokMessagesTable.createNewTableMessage(
            SendMessageData(
                chatId = chatId,
                aiModelId = modelId,
                userID = account.getUserId(),
                textMessage = userMessage,
                senderId = account.getUserId(),
                status = "Delivered"
            )
        )

        sendMessageToAi(aiModelId = modelId, chatId = chatId, text = aiContext, newChat = false)
    }


    @Suppress("SuspiciousIndentation")
    private suspend fun sendMessageToAi(aiModelId: Long, chatId: Long, text: String, newChat: Boolean) {
        val account = getAccountManager()
        val aiResponse = mokMessagesTable.createNewTableMessage(
            SendMessageData(
                chatId = chatId,
                aiModelId = aiModelId,
                userID = account.getUserId(),
                textMessage = "",
                status = "Pending",
                senderId = -1
            )
        )

                wrapResponse<team.platforma.chat_data.network.response.SendMessageResponse>(
                    client.request("web-api/chat/$chatId/message/send") {
                        contentType(ContentType.Application.Json)
                        header("Authorization", "Bearer ${account.getUserAuthToken()}")
                        setBody(
                            MessageBody(
                                content = text
                            )
                        )
                        method = HttpMethod.Post
                    }
                ).onSuccess { data, code ->


                    pullingManager.start(
                        client = client,
                        chatId = chatId,
                        scope = pollingScope,
                        tableMessageId = aiResponse,
                        token = account.getUserAuthToken() ?: "",
                        messageId = data.message_id,
                        onPulling = { message ->

                            if (message.isNotEmpty()) {
                                mokMessagesTable.updateMessageText(
                                    chatId = chatId,
                                    tableMessageId = aiResponse,
                                    status = "Printing",
                                    text = message
                                )
                            }
                        },
                        onStop = { message ->
                            mokMessagesTable.updateMessageText(
                                chatId = chatId,
                                tableMessageId = aiResponse,
                                status = "Delivered",
                                text = message
                            )
                            if (newChat) {
                               chatsRepository.insertChat(
                                   ChatData(
                                       id = chatId,
                                       aiModelId = aiModelId,
                                       userId = account.getUserId(),
                                       name = text
                                   )
                               )
                            }
                            // save chat
                        }
                    )

                }


    }


    class ChatPollingManager : ResponseWrapper() {
        private var pollingJob: Job? = null
        private var currentText = StringBuilder()
        private var _tableMessageId: Long? = null
        private var _chatId: Long? = null


        fun start(
            client: HttpClient,
            chatId: Long,
            intervalMillis: Long = 500L,
            tableMessageId: Long,
            scope: CoroutineScope,
            token: String,
            messageId: Long,
            onPulling: suspend (String) -> Unit,
            onStop: suspend (String) -> Unit
        ) {
            _chatId = chatId
            _tableMessageId = tableMessageId



            if (pollingJob != null) return // Уже запущено
            currentText.clear()

            pollingJob = scope.launch {
                while (isActive) {
                    try {

                        val response = wrapResponse<List<MessageResponse>>(
                            client.request("/web-api/chat/$chatId/message") {
                                contentType(ContentType.Application.Json)
                                header("Authorization", "Bearer $token")
                                parameter("from_message_id", messageId)
                                parameter("limit", 1)
                            }
                        )

                        when (response) {
                            is Response.Success -> {
                                val newMessage = response.data.first()
                                fullText = newMessage.content

                                if (
                                    fullText.startsWith(currentText.toString()) &&
                                    fullText.length > currentText.length
                                ) {
                                    val newPart = fullText.substring(currentText.length)
                                    for (char in newPart) {
                                        currentText.append(char)
                                        onPulling(currentText.toString())
                                        delay(10L)
                                    }
                                } else if (
                                    fullText.isNotEmpty() &&
                                    fullText == currentText.toString()
                                ) {
//                                    println("✅ Сообщение полностью получено.")
                                    onStop(fullText)
                                    pollingJob?.cancel()
                                    pollingJob = null
                                }
                            }

                            is Response.Fail -> {
                                println("Polling fail: ${response.message}")
                            }

                            is Response.Exception -> {
                                println("Polling error: ${response.message}")
                            }
                        }

                    } catch (e: Exception) {
                        println("Exception in polling: ${e.message}")
                    }

                    delay(intervalMillis)
                }
            }
        }

        var fullText = ""
        suspend fun stop(onStop: suspend (tableId: Long?, chatId: Long?, text: String) -> Unit) {
            pollingJob?.cancel()
            pollingJob = null
            onStop(_tableMessageId, _chatId, fullText)
        }
    }


//    private val mokAiText = "Конечно, добавлю заголовки для каждого раздела:\n\n---\n\n## 1. Добавление зависимости\n\nУбедитесь, что у вас установлены необходимые зависимости для Android, так как `AccountManager` является частью Android SDK.\n\n---\n\n## 2. Запрос разрешений\n\nВам может понадобиться добавить разрешения в ваш манифест, чтобы работать с аккаунтами:\n\n```xml\n<uses-permission android:name=\"android.permission.GET_ACCOUNTS\" />\n```\n\n---\n\n## 3. Получение экземпляра AccountManager\n\n```kotlin\nval accountManager = AccountManager.get(context)\n```\n\nГде `context` - это контекст вашего приложения или активности.\n\n---\n\n## 4. Получение списка аккаунтов\n\n```kotlin\nval accounts = accountManager.accounts\nfor (account in accounts) {\n    println(\"Account name: \"$\"{account.name}, Account type: \"$\"{account.type}\")\n}\n```\n\n---\n\n## 5. Добавление новой учетной записи\n\nДобавление новой учетной записи требует специфичного для вашего приложения / сервиса метода, так как AccountManager по умолчанию не поддерживает произвольное добавление аккаунтов без аутентификации.\n\n---\n\n## 6. Получение токенов аутентификации\n\nЕсли ваше приложение работает с аутентифицированными сервисами, вы можете запросить токены:\n\n```kotlin\naccountManager.getAuthToken(\n    yourAccount,                 // Экземпляр Account, который вы хотите использовать\n    \"authTokenType\",             // Тип токена, который вы запрашиваете\n    null,                        // Дополнительные опции\n    this,                        // Activity или контекст\n    { future ->\n        try {\n            val token = future.result.getString(AccountManager.KEY_AUTHTOKEN)\n            // Используйте токен по необходимости\n        } catch (e: Exception) {\n            e.printStackTrace()\n        }\n    },\n    null                         // Обработчик ошибок, по желанию\n)\n```\n\n---\n\n## 7. Удаление аккаунта\n\nУдаление аккаунта может быть выполнено через метод `removeAccount`:\n\n```kotlin\naccountManager.removeAccount(\n    yourAccount,                 // Экземпляр Account, который вы хотите удалить\n    this,                        // Activity или контекст\n    { future ->\n        try {\n            if (future.result) {\n                println(\"Account removed successfully.\")\n            }\n        } catch (e: Exception) {\n            e.printStackTrace()\n        }\n    },\n    null                         // Обработчик ошибок, по желанию\n)\n```\n\n---\n\nИмейте в виду, что работа с учетными записями может различаться в зависимости от версии Android и конкретного API, который ваше приложение пытается реализовать. Всегда следуйте официальной документации Android для получения актуальной информации и примеров."
//    private val mokAiText = "Вот исправленный вариант твоего текста с корректными заголовками и структурой:\n\n---\n\n# Полезные ссылки по работе с AccountManager в Android-приложениях на Kotlin\n\nНиже приведены полезные ссылки и ресурсы, посвященные работе с компонентом `AccountManager`, а также примеры практического использования этого инструмента в Android-приложениях на языке программирования Kotlin.\n\n---\n\n## 📖 Официальная документация\n\n- **AccountManager API:**  \n  [Official Documentation](https://developer.android.com/reference/android/app/AccountManager)  \n  Описание функциональности класса `AccountManager`, его методов и возможностей.\n\n---\n\n## 🛠 Практические руководства и туториалы\n\n- **Интеграция Google OAuth2 с помощью AccountManager:**  \n  [Medium Article](https://medium.com/@jacksawyer87/android-using-the-google-oauth2-token-from-the-accountmanager-5d6b64c4930a)  \n  Пошаговый пример интеграции Google OAuth2 через `AccountManager` на Kotlin.\n\n- **Практическое использование AccountManager на Kotlin:**  \n  [GitHub Sample](https://github.com/googlesamples/android-SignIn/blob/master/signin-demo-app/src/main/java/com/google/android/gms/samples/signindemo/MainActivity.kt)  \n  Пример проекта с открытым исходным кодом от Google, демонстрирующий использование AccountManager.\n\n---\n\n## 🌍 Платформы и сервисы\n\n- **Авторизация пользователей через Google Sign-In:**  \n  [Google Identity Platform](https://developers.google.com/identity/sign-in/android/)  \n  Подробное руководство по настройке Google авторизации и интеграции с AccountManager.\n\n---\n\n## 🎯 Общая информация по безопасности и аутентификации\n\n- **Руководство по аутентификации в Android:**  \n  [Developer.Android.Com](https://developer.android.com/guide/authentication/index.html)  \n  Общие рекомендации и лучшие практики по организации безопасной аутентификации пользователей в Android-приложении.\n\n---\n\nНадеюсь, теперь ссылки стали нагляднее и удобнее для восприятия!"
//    private val mokAiText = "Вот подробная инструкция по работе с компонентом `AccountManager` в Kotlin:\n\n### 1. Запуск проекта и подготовка\n\n#### Шаг 1. Зависимости\n\nУбедитесь, что в вашем проекте настроены необходимые зависимости. В файле `build.gradle` (модуль приложения) добавьте зависимость от библиотеки AndroidX Account Manager:\n\n```groovy\ndependencies {\n    implementation 'androidx.accountmgr:account-manager:1.0.0'  \n}\n```\n\n#### Шаг 2. Разрешение работы с аккаунтами\n\nВ манифесте (`AndroidManifest.xml`) укажите разрешение на работу с аккаунтами:\n\n```xml\n<uses-permission android:name=\"android.permission.GET_ACCOUNTS\"/>\n```\n\n### 2. Использование AccountManager\n\n#### Шаг 1. Создание объекта AccountManager\n\n```kotlin\nval accountManager = AccountManager.get(this /* Контекст приложения */)\n```\n\n#### Шаг 2. Получение списка аккаунтов\n\nЗапросите список существующих аккаунтов Google на устройстве:\n\n```kotlin\nval accounts = accountManager.accounts\nfor (account in accounts) {\n    Log.d(\"AccountManager\", \"Found account: \"$\"{account.name}\")\n}\n```\n\n#### Шаг 3. Предложение пользователю выбрать аккаунт\n\nСоздайте намерение (Intent) для отображения диалога выбора аккаунта:\n\n```kotlin\nval intent = Intent(AccountManager.ACTION_PICK_ACCOUNT)\nintent.type = AccountManager.KEY_ACCOUNT_TYPE_GOOGLE\nstartActivityForResult(intent, REQUEST_CODE_PICK_ACCOUNT)\n```\n\nОбработайте результат выбора аккаунта в методе `onActivityResult`:\n\n```kotlin\noverride fun onActivityResult(requestCode: Int, resultCode: Int, data: Intent?) {\n    super.onActivityResult(requestCode, resultCode, data)\n    \n    when (requestCode) {\n        REQUEST_CODE_PICK_ACCOUNT -> {\n            if (resultCode == RESULT_OK) {\n                val selectedAccount = data?.getParcelableExtra<Account>(AccountManager.KEY_ACCOUNT_NAME)!!\n                Log.d(\"AccountManager\", \"Selected account: \"$\"{selectedAccount.name}\")\n                \n                // Например, получаем токен аутентификации выбранного аккаунта\n                getAuthToken(selectedAccount)\n            } else {\n                Log.w(\"AccountManager\", \"No account was selected.\")\n            }\n        }\n    }\n}\n```\n\nГде константа `REQUEST_CODE_PICK_ACCOUNT` задана вами самостоятельно:\n\n```kotlin\nprivate const val REQUEST_CODE_PICK_ACCOUNT = 123\n```\n\n#### Шаг 4. Получение токена аутентификации\n\nИспользуйте метод `getAuthToken` для получения токена, необходимого для взаимодействия с Google-сервисами:\n\n```kotlin\nfun getAuthToken(account: Account) {\n    val callback = object : AccountManagerCallback<Bundle> {\n        override fun run(bundle: Bundle?) {\n            val token = bundle!![AccountManager.KEY_AUTHTOKEN]\n            Log.d(\"AccountManager\", \"Got auth token: \"$\"token\")\n            \n            // Дальнейшая работа с токеном\n        }\n    }\n\n    accountManager.getAuthToken(\n        account,\n        \"oauth2:https://www.googleapis.com/auth/userinfo.profile\",\n        null,\n        this,\n        callback,\n        null\n    )\n}\n```\n\nЗдесь параметр `\"oauth2:...\"` — это требуемый scope доступа к Google API (например, профиль пользователя).\n\n---\n\nТаким образом, вы получите полный цикл работы с аккаунтами Google в приложении на Kotlin с помощью компонента `AccountManager`."

    //private val mokAiText ="Рассмотрим задачу заново подробно и аккуратно.\n\n### Условие задачи:\nТело движется вверх по наклонной плоскости с начальной скоростью $v_0$. От основания наклонной плоскости тело прошло высоту $h = 0{,}5$ метра дважды: один раз через $t_1 = 0{,}5$ секунды после начала движения, второй раз — через $t_2 = 2{,}5$ секунды после начала движения. Требуется найти начальную скорость $v_0$.\n\n### Решение:\n\n#### Шаг 1. Запишем уравнение движения по наклонной плоскости.\n\nДвижение вверх по наклонной плоскости является равнозамедленным движением. Общее уравнение для положения тела в любой момент времени имеет вид:\n\n$$\nh(t) = v_0 t - \\frac{gt \\sin \\alpha}{2},\n$$\n\nгде:\n- $h(t)$ — высота над основанием плоскости в момент времени $t$,\n- $v_0$ — начальная скорость тела,\n- $g$ — ускорение свободного падения ($g \\approx 9{,}81 \\, \\text{м/с}^2$),\n- $\\alpha$ — угол наклона плоскости.\n\nОднако, угол $\\alpha$ в условии не дан, зато известно, что тело достигло одной и той же высоты дважды, что упрощает решение.\n\n#### Шаг 2. Условие достижения одинаковой высоты дважды.\n\nИз условия задачи следует, что высота $h(t_1) = h(t_2) = 0{,}5$ м. Тогда получаем систему уравнений:\n\n$$\nv_0 t_1 - \\frac{a}{2} t_1^2 = v_0 t_2 - \\frac{a}{2} t_2^2,\n$$\n\nгде $a = g \\sin \\alpha$ — ускорение тела вниз по наклонной плоскости.\n\nПодставим известные значения ($t_1 = 0{,}5$ с, $t_2 = 2{,}5$ с):\n\n$$\nv_0 \\cdot 0{,}5 - \\frac{a}{2} \\cdot (0{,}5)^2 = v_0 \\cdot 2{,}5 - \\frac{a}{2} \\cdot (2{,}5)^2.\n$$\n\nУпрощаем выражение:\n\n$$\n0{,}5v_0 - \\frac{a}{8} = 2{,}5v_0 - \\frac{6{,}25a}{2}.\n$$\n\nДомножим обе части уравнения на 8, чтобы избавиться от знаменателей:\n\n$$\n4v_0 - a = 20v_0 - 25a.\n$$\n\nПереносим члены с $a$ в одну сторону, а с $v_0$ — в другую:\n\n$$\n24a = 16v_0.\n$$\n\nПолучаем соотношение:\n\n$$\na = \\frac{2}{3}v_0.\n$$\n\n#### Шаг 3. Подставим найденное ускорение в уравнение высоты.\n\nВернёмся к исходному уравнению высоты, подставив $a = \\frac{2}{3}v_0$:\n\n$$\nh(t) = v_0 t - \\frac{\\frac{2}{3}v_0}{2} t^2 = v_0 t - \\frac{v_0}{3} t^2.\n$$\n\nИспользуя условие, что $h(t_1) = 0{,}5$ м при $t_1 = 0{,}5$ с:\n\n$$\n0{,}5 = v_0 \\cdot 0{,}5 - \\frac{v_0}{3} \\cdot (0{,}5)^2.\n$$\n\nРаскроем скобки:\n\n$$\n0{,}5 = 0{,}5v_0 - \\frac{v_0}{12}.\n$$\n\nВыносим $v_0$ за скобки:\n\n$$\n0{,}5 = v_0 \\left(0{,}5 - \\frac{1}{12}\\right) = v_0 \\cdot \\frac{5}{12}.\n$$\n\nРешаем уравнение относительно $v_0$:\n\n$$\nv_0 = \\frac{0{,}5}{\\frac{5}{12}} = 0{,}5 \\cdot \\frac{12}{5} = 1{,}2 \\, \\text{м/с}.\n$$\n\n### Ответ:\n\n$$\n\\boxed{1{,}2 \\, \\text{м/с}}\n$$"


//    init {
//        CoroutineScope(Dispatchers.Default).launch {
//            mokMessagesTable.createNewTableMessage(
//                SendMessageData(
//                    chatId = 0,
//                    aiModelId = 2,
//                    userID = 2,
//                    textMessage = mokAiText,
//                    status = "Delivered",
//                    senderId = -1
//                )
//            )
//        }
//
//    }

    private suspend fun setMokAiMessage(chatId: Long, aiModelId: Long, userId: Long) {
        delay(5000)
        mokMessagesTable.createNewTableMessage(
            SendMessageData(
                chatId = chatId,
                aiModelId = aiModelId,
                userID = userId,
                textMessage = "С наступлением лета пожилые россияне получили новые возможности для активной и насыщенной жизни — теперь центры долголетия перешли на круглосуточный режим.Pro Город Пермь\n" +
                        "Утро можно начать с зарядки или дыхательных практик на свежем воздухе.Сиб.фм\n" +
                        "Для тех, кто хочет проводить больше времени с семьёй, предусмотрены специальные программы совместного досуга с внуками: оборудованные пространства делают творчество и спорт доступными для всех возрастов.Сиб.фм\n" +
                        "Исследования показывают: регулярные занятия помогают снизить риск возрастных заболеваний, улучшить самочувствие и настроение.Pro Город Пермь" +
                        "\n" +
                        "С наступлением лета пожилые россияне получили новые возможности для активной и насыщенной жизни — теперь центры долголетия перешли на круглосуточный режим.Pro Город Пермь\n" +
                        "Утро можно начать с зарядки или дыхательных практик на свежем воздухе.Сиб.фм\n" +
                        "Для тех, кто хочет проводить больше времени с семьёй, предусмотрены специальные программы совместного досуга с внуками: оборудованные пространства делают творчество и спорт доступными для всех возрастов.Сиб.фм\n" +
                        "Исследования показывают: регулярные занятия помогают снизить риск возрастных заболеваний, улучшить самочувствие и настроение.Pro Город Пермь" +
                        "\n" +
                        "С 1 сентября медики получат возможность выписывать пациентам добавки из перечня разрешенных в качестве дополнения к основной терапии.Кореновские вести\n" +
                        "Согласно новому документу теперь Роспотребнадзор наделен правом оперативно блокировать доступ к сайтам, которые распространяют информацию о БАДах, находящихся под запретом.Кореновские вести\n" +
                        "Теперь изъятие незарегистрированных или опасных для здоровья добавок из незаконного оборота будет моментальным.Кореновские вести\n" +
                        "Крупнейшие отечественные производители БАДов размещаются на Дальнем Востоке, Алтае, в Сибири и Краснодарском крае.Кореновские вести",
                status = "Delivered",
                senderId = -1
            )
        )
    }

    override suspend fun updateMessage(chatId: Long, tableMessageId: Long, status: String) {
        mokMessagesTable.updateMessage(chatId, tableMessageId, status)
    }

    override fun observeMessages(aiModelId: Long?, chatId: Long): Flow<List<MokMessage>> =
        mokMessagesTable.observeMessages(aiModelId, getAccountManager().getUserId(), chatId)

    override suspend fun stopGeneration() {
        pullingManager.stop { tableId, chatId, text ->
            if (tableId != null && chatId != null) {
                if (text.isNotEmpty()) {
                    mokMessagesTable.updateMessageText(
                        chatId = chatId,
                        tableMessageId = tableId,
                        status = "Delivered",
                        text = text
                    )
                } else {
                    mokMessagesTable.deleteMessage(tableId)
                }
            }
        }
    }

}

//class PollingManager(
//    private val client: HttpClient,
//    private val chatId: Long,
//    private val intervalMillis: Long = 5000L
//) {
//    private var pollingJob: Job? = null
////    private var lastTimestamp: Long = 0
//
//    fun start(scope: CoroutineScope, onMessage: (List<ChatMessage>) -> Unit) {
//        pollingJob?.cancel()
//        pollingJob = scope.launch {
//            while (isActive) {
//                try {
//                    val messages: List<ChatMessage> = client.get("https://yourserver.com/chat/updates") {
////                        parameter("chatId", chatId)
////                        parameter("since", lastTimestamp)
//                    }
//
//                    if (messages.isNotEmpty()) {
//                        onMessage(messages)
////                        lastTimestamp = messages.maxOf { it.timestamp }
//                    }
//                } catch (e: Exception) {
//                    println("Polling error: ${e.message}")
//                }
//
//                delay(intervalMillis)
//            }
//        }
//    }
//
//    fun stop() {
//        pollingJob?.cancel()
//        pollingJob = null
//    }
//}